import { canonicalObjectRef, isSameObjectRef, type CanonicalObjectRef } from '@/shared/canonical-object-key';
import {
  FILE_PREVIEW_TELEMETRY_PATH,
  type BrowserErrorBody,
  type IdentityKind,
  type PreviewEventBody,
  type PreviewState,
} from '@/shared/file-preview-telemetry';

export type PreviewIdentity = {
  identityKind: IdentityKind;
  fileUuid: string | null;
  key: string | null;
};

const ERROR_DEDUPE_MS = 60_000;
const MAX_ERROR_REPORTS_PER_PAGE = 20;

function post(body: PreviewEventBody | BrowserErrorBody): void {
  try {
    void fetch(FILE_PREVIEW_TELEMETRY_PATH, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {});
  } catch {
    // Telemetry never throws into the viewer.
  }
}

export function extensionFromRef(ref: string | null | undefined): string | null {
  const canonical = canonicalObjectRef(ref);
  const name = canonical?.key.split('/').pop() ?? '';
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1 ? name.slice(dot + 1).toLowerCase() : null;
}

/**
 * The cited object's identity. A file id counts only when the file record's own storage URL is
 * exactly the cited object. Any other file id can describe a parent container.
 */
export function resolvePreviewIdentity(input: {
  citedRef: string | null | undefined;
  fileUuid?: string | null;
  fileUrl?: string | null;
}): PreviewIdentity {
  const cited = canonicalObjectRef(input.citedRef);
  const recorded = canonicalObjectRef(input.fileUrl);
  const uuid = (input.fileUuid ?? '').trim();
  if (uuid && isSameObjectRef(cited, recorded)) {
    return { identityKind: 'file_uuid', fileUuid: uuid, key: null };
  }
  if (cited) {
    return {
      identityKind: cited.kind === 'archive_member' ? 'archive_member_sha256' : 's3_key_sha256',
      fileUuid: null,
      key: cited.key,
    };
  }
  return { identityKind: 'none', fileUuid: null, key: null };
}

export function reportUnsupportedPreview(input: {
  state: PreviewState;
  identity: PreviewIdentity;
  caseId?: string | null;
  mime?: string | null;
  extension?: string | null;
}): void {
  post({
    event: 'file_preview_unsupported',
    state: input.state,
    identity_kind: input.identity.identityKind,
    file_uuid: input.identity.fileUuid,
    key: input.identity.key,
    case_id: input.caseId ?? null,
    mime: input.mime ?? null,
    extension: input.extension ?? null,
  });
}

const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

/** Counts a video the browser cannot decode. Network and abort errors are not format failures. */
export function reportVideoFormatFailure(input: {
  url: string;
  caseId?: string | null;
  errorCode: number | undefined;
}): void {
  if (input.errorCode !== MEDIA_ERR_SRC_NOT_SUPPORTED) return;
  reportUnsupportedPreview({
    state: 'video_error',
    identity: resolvePreviewIdentity({ citedRef: input.url }),
    caseId: input.caseId,
    extension: extensionFromRef(input.url),
  });
}

/**
 * Opening identity for the latch: one report per cited object, per viewer variant. The key alone
 * names the object, so a bare path and an `s3://` URL for it match. A ref that is not an S3
 * object keeps its own text, so two such refs never share one identity.
 */
export function openingIdentity(citedRef: string | null | undefined, variant: string): string {
  const cited: CanonicalObjectRef | null = canonicalObjectRef(citedRef);
  return `${variant}|${cited ? cited.key : (citedRef ?? '').trim()}`;
}

export function browserErrorName(reason: unknown): string {
  if (reason instanceof Error) return reason.name || 'Error';
  if (reason && typeof reason === 'object' && typeof (reason as { name?: unknown }).name === 'string') {
    return (reason as { name: string }).name;
  }
  return 'NonErrorRejection';
}

let errorReports = 0;
const lastErrorAt = new Map<string, number>();

export function reportBrowserError(kind: BrowserErrorBody['kind'], errorName: string, now = Date.now()): void {
  if (errorReports >= MAX_ERROR_REPORTS_PER_PAGE) return;
  const dedupeKey = `${kind}:${errorName}`;
  const last = lastErrorAt.get(dedupeKey);
  if (last !== undefined && now - last < ERROR_DEDUPE_MS) return;
  lastErrorAt.set(dedupeKey, now);
  errorReports += 1;
  post({ event: 'browser_uncaught_error', kind, error_name: errorName });
}
